import moment from "moment-timezone";

let warnedMissingTz = false

function getGroup(){
  const raw = localStorage.getItem('group')
  if(!raw) return null
  try { return JSON.parse(raw) } catch(e) { return null }
}

function resolveGroupTz(group){
  if(group && group.tz_name) return group.tz_name
  if(group && typeof group.timezone === 'string'){
    const candidate = group.timezone.split(' - ')[0].trim()
    if(candidate && moment.tz.zone(candidate)) return candidate
  }
  if(!warnedMissingTz){
    // eslint-disable-next-line no-console
    console.warn('[dates] group.tz_name missing; falling back to browser timezone')
    warnedMissingTz = true
  }
  return moment.tz.guess()
}

function toGroupMoment(input, tz){
  if(input == null || input === '') return null
  if(typeof input === 'string'){
    if(/^\d{4}-\d{2}-\d{2}$/.test(input)){
      return moment(input, 'YYYY-MM-DD')
    }
    if(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(input)){
      input = input.replace(' ', 'T') + 'Z'
    }
  }
  const m = moment.utc(input)
  if(!m.isValid()) return null
  return tz ? m.tz(tz) : m
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * Format a UTC timestamp into the company group's local date string.
 * Reads tz_name (or parses from `timezone` display string), date_format,
 * and show_day_of_week from localStorage.group on every call.
 *
 * Pass { dayOfWeek: false } to suppress the weekday prefix regardless of
 * the group's show_day_of_week setting (e.g. CSV export).
 */
function formatGroupDate(input, opts = {}){
  const showDow = opts.dayOfWeek !== false
  const group = getGroup()
  if(!group) return ''
  const m = toGroupMoment(input, resolveGroupTz(group))
  if(!m) return ''
  const wallDate = m.format('YYYY-MM-DD')
  const datePart = getDate(wallDate, group.date_format || 'MM/DD/YYYY')
  const prefix = showDow && group.show_day_of_week ? WEEKDAYS[m.day()] + ', ' : ''
  return prefix + datePart
}

/**
 * Format a UTC timestamp into the company group's local time string.
 * Reads tz_name and time_format from localStorage.group on every call.
 */
function formatGroupTime(input){
  const group = getGroup()
  if(!group) return ''
  const m = toGroupMoment(input, resolveGroupTz(group))
  if(!m) return ''
  const hours = String(m.hours()).padStart(2, '0')
  const mins = String(m.minutes()).padStart(2, '0')
  return getTime(`${hours}:${mins}`, group.time_format)
}

function getLocalDateTime(d, date_format, time_format){
  if(time_format === 'hh:mm'){
    time_format += ' A'
  }
  const format = date_format + ', ' + time_format
  return moment(d).format(format)
}

/**
 * Compact date+time for tight UI (badges, chips, narrow cards). Uses the
 * group's date_format and time_format but strips the year token (and its
 * adjacent separator) from the date so the output stays narrow. Preserves
 * the group's day/month order and separator preferences.
 *
 *   'MM-DD-YYYY' + '12h'    → 'MM-DD, hh:mm A'   (US, e.g. '05-14, 09:26 AM')
 *   'DD/MM/YYYY' + 'HH:mm'  → 'DD/MM, HH:mm'     (EU, e.g. '14/05, 09:26')
 *   'DD-MMM-YYYY' + '12h'   → 'DD-MMM, hh:mm A'  (e.g. '14-May, 09:26 AM')
 */
function getShortDateTime(d, date_format, time_format){
  if(!d) return ''
  const dateFmt = (date_format || 'MM-DD-YYYY')
    .replace(/Y{2,4}/g, '')
    .replace(/^[-./, ]+|[-./, ]+$/g, '')
    .trim()
  const timeFmt = getPickerTimeFormat(time_format)
  return moment(d).format(`${dateFmt}, ${timeFmt}`)
}

function getDate(d, date_format){
  return moment(d, 'YYYY-MM-DD').format(date_format)
}

function getTime(t, time_format){
  if(time_format === 'hh:mm'){
    time_format = "hh:mm A"
  }
  const time = moment(`1970-01-01T${t}`, 'YYYY-MM-DDTHH:mm:ss');
  return time.format(time_format);
}

/**
 * Convert a moment-style date format (e.g. 'MM-DD-YYYY') to the token format
 * used by el-date-picker (e.g. 'MM-dd-yyyy'). Separator-agnostic.
 */
function getPickerDateFormat(group_date_format){
  if(!group_date_format) return 'MM/dd/yyyy'
  return group_date_format.replace(/Y/g, 'y').replace(/D/g, 'd')
}

/**
 * Resolve the group's time_format ('12h' or 'HH:mm') into the moment token
 * the el-time-picker (and any moment.format call) expects.
 */
function getPickerTimeFormat(group_time_format){
  if(group_time_format === 'HH:mm' || group_time_format === '24h') return 'HH:mm'
  return 'hh:mm A'
}

/**
 * Parse a time value of ANY shape into a JS Date (time-of-day set on today).
 * Returns null if the input can't be parsed.
 *
 * Handles:
 *   - Date objects (pass-through when valid)
 *   - 24h strings: "09:00", "09:00:00", "09:00:00.000"
 *   - 12h strings: "9:00 AM", "09:00 PM", "9:00:00 am"
 *   - ISO timestamps: "2026-04-22T09:00:00.000Z", "2026-04-22 09:00:00"
 *
 * Used for el-time-picker v-model binding, where the picker expects a Date
 * whose hours/minutes reflect the intended time.
 */
function parseTime(value){
  if(!value) return null
  if(value instanceof Date){
    return isNaN(value.getTime()) ? null : value
  }
  let str = String(value).trim()
  // If there's an ISO/datetime prefix, keep just the time portion.
  if(str.includes('T')) str = str.split('T')[1]
  else if(/\d{4}-\d{2}-\d{2}\s/.test(str)) str = str.split(/\s+/).slice(1).join(' ')

  const formats = [
    'HH:mm:ss.SSS',
    'HH:mm:ss',
    'HH:mm',
    'hh:mm:ss A',
    'hh:mm A',
    'h:mm:ss A',
    'h:mm A',
    moment.ISO_8601
  ]
  const m = moment(str, formats, true)
  if(!m.isValid()) return null
  const date = new Date()
  date.setHours(m.hour(), m.minute(), m.second(), 0)
  return date
}

/**
 * Returns true if the value is a parseable, non-zero date.
 * Handles null/undefined/empty, MySQL zero dates ('0000-00-00', '0000-00-00 00:00:00'),
 * and unparseable strings.
 */
function isValidDate(d){
  if(!d) return false
  const s = String(d)
  if(s.startsWith('0000-')) return false
  const t = new Date(s).getTime()
  return !isNaN(t) && t > 0
}

export {
  getLocalDateTime as localDateTime,
  getShortDateTime as shortDateTime,
  getDate as date,
  getTime as time,
  getPickerDateFormat as pickerDateFormat,
  getPickerTimeFormat as pickerTimeFormat,
  parseTime,
  isValidDate,
  formatGroupDate,
  formatGroupTime
}
